'use client';

import dynamic from 'next/dynamic';
import type { ComponentProps } from 'react';
import cn from '@/lib/packages/cn';

type MuxPlayerReactProps = ComponentProps<typeof import('@mux/mux-player-react')['default']>;
const MuxPlayer = dynamic<MuxPlayerReactProps>(() => import('@mux/mux-player-react'), { ssr: false });

const buildPosterUrl = (playbackId: string, posterSecond: number, width = 800) => {
  const time = Number.isFinite(posterSecond) ? Math.max(0, posterSecond) : 10;
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?time=${time}&width=${width}&format=webp`;
};

type MuxVideoProps = {
  playbackId: string;
  playbackToken?: string; // for signed playback
  className?: string;
  title?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  aspectRatio?: string;
  posterSecond?: number; // time in seconds for poster frame (default 10)
  posterWidth?: number; // poster width in px (default 800)
  accentColor?: string; // overrides Mux default pink
  primaryColor?: string; // icon color
  preload?: 'auto' | 'metadata' | 'none';
};

export default function MuxVideo({
  playbackId,
  playbackToken,
  className,
  title,
  autoPlay = false,
  muted = false,
  loop = false,
  aspectRatio = '16/9',
  posterSecond = 10,
  posterWidth = 800,
  accentColor = 'var(--info)',
  primaryColor = '#ffffff',
  preload = 'none',
}: MuxVideoProps) {
  const poster = buildPosterUrl(playbackId, posterSecond, posterWidth);

  return (
    <div className={cn('relative w-full overflow-hidden rounded-lg', className)} style={{ aspectRatio }}>
      <MuxPlayer
        playbackId={playbackId}
        tokens={playbackToken ? { playback: playbackToken } : undefined}
        streamType='on-demand'
        metadata={{
          video_title: title || 'Video',
        }}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        className='w-full h-full'
        poster={poster}
        accentColor={accentColor}
        primaryColor={primaryColor}
        preload={preload}
        playsInline
      />
    </div>
  );
}
